import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Avatar,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  OutlinedInput,
  ListItemText,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Restore as RestoreIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  ViewList as ViewListIcon,
  ViewKanban as ViewKanbanIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { cpeService } from '../services/cpeService';
import nextActionOptionService from '../services/nextActionOptionService';
import { CPEFormDialog } from '../components/CPEFormDialog';
import CPEBoardView from '../components/CPEBoardView';
import zoneService from '../services/zoneService';
import { userService } from '../services/userService';
import {
  CPE,
  CPEFilters,
  CPE_STATUSES,
  COMMERCIAL_STATUSES,
  PLANT_TYPES,
  PAGE_SIZE_OPTIONS,
  PlantType,
  CPEStatus,
  CommercialStatus,
  InternalPositioning,
  NextActionOption,
} from '../types/cpe';
import { User } from '../types/user';

export const CPEListPage = () => {
  console.log('CPEListPage rendering');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [cpes, setCPEs] = useState<CPE[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [formDialogOpen, setFormDialogOpen] = useState(false);

  // Filters — pre-populate from navigation state (e.g. click-through from dashboard)
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<CPEFilters>(() => {
    const navState = location.state as { filters?: CPEFilters } | null;
    return navState?.filters || {};
  });

  // Report generation
  const [generatingReport, setGeneratingReport] = useState(false);

  // Delete/Restore confirmation dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedCPEId, setSelectedCPEId] = useState<string | null>(null);
  const [selectedCPEName, setSelectedCPEName] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  const [zones, setZones] = useState<{ id: string; name: string }[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [nextActionOptions, setNextActionOptions] = useState<NextActionOption[]>([]);

  // Board view
  const [viewMode, setViewMode] = useState<'list' | 'board'>(() =>
    (localStorage.getItem('cpe-view-mode') as 'list' | 'board' | null) ?? 'list'
  );
  const [boardCPEs, setBoardCPEs] = useState<CPE[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardError, setBoardError] = useState('');

  const zoneMap: Record<string, string> = Object.fromEntries(
    zones.map((z) => [z.id, z.name])
  );

  const loadCPEs = useCallback(async () => {
    try {
      console.log('Loading CPEs with params:', { page: page + 1, rowsPerPage, filters });
      setLoading(true);
      setError('');
      const response = await cpeService.getCPEs(page + 1, rowsPerPage, filters);
      console.log('CPEs loaded successfully:', response);
      setCPEs(response.data.cpes);
      setTotal(response.data.pagination.total);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      console.error('Failed to load CPEs:', err);
      setError(error.response?.data?.message || 'Failed to load CPEs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const loadCPEsForBoard = useCallback(async () => {
    try {
      setBoardLoading(true);
      setBoardError('');
      const response = await cpeService.getCPEs(1, 3000, filters);
      setBoardCPEs(response.data.cpes);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setBoardError(error.response?.data?.message || 'Failed to load PPs');
    } finally {
      setBoardLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (viewMode === 'board') {
      loadCPEsForBoard();
    } else {
      loadCPEs();
    }
  }, [viewMode, loadCPEs, loadCPEsForBoard]);

  const handleViewModeChange = (mode: 'list' | 'board') => {
    setViewMode(mode);
    localStorage.setItem('cpe-view-mode', mode);
  };

  const handleBoardStatusChange = (cpeId: string, newStatus: CommercialStatus) => {
    setBoardCPEs((prev) =>
      prev.map((c) => (c.id === cpeId ? { ...c, commercial_status: newStatus } : c))
    );
  };


  // Load zones and users for filters
  useEffect(() => {
    const loadZones = async () => {
      try {
        const zonesList = await zoneService.getZonesList();
        setZones(zonesList.map(z => ({ id: z.id, name: z.name })));
      } catch (error) {
        console.error('Error loading zones:', error);
      }
    };
    const loadUsers = async () => {
      try {
        const usersList = await userService.getAllUsers();
        setUsers(usersList);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    const loadNextActionOptions = async () => {
      try {
        const opts = await nextActionOptionService.getAll();
        setNextActionOptions(opts);
      } catch (error) {
        console.error('Error loading next action options:', error);
      }
    };
    loadZones();
    loadUsers();
    loadNextActionOptions();
  }, []);

  const handleSearch = () => {
    setFilters({ ...filters, search: searchTerm || undefined });
    setPage(0);
  };

  const handleFilterChange = (key: keyof CPEFilters, value: string | number | PlantType[] | CPEStatus[] | CommercialStatus[] | InternalPositioning[] | string[] | boolean | undefined) => {
    // Preserve explicit boolean false and number 0 as valid filter values
    const normalizedValue = value === undefined || value === '' || (Array.isArray(value) && value.length === 0) ? undefined : value;
    setFilters({ ...filters, [key]: normalizedValue });
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id: string, name: string) => {
    setSelectedCPEId(id);
    setSelectedCPEName(name);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCPEId) return;

    try {
      setDeleting(true);
      await cpeService.deleteCPE(selectedCPEId);
      setDeleteDialogOpen(false);
      setSelectedCPEId(null);
      setSelectedCPEName('');
      loadCPEs();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to delete CPE');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (id: string, name: string) => {
    setSelectedCPEId(id);
    setSelectedCPEName(name);
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!selectedCPEId) return;

    try {
      setRestoring(true);
      await cpeService.restoreCPE(selectedCPEId);
      setRestoreDialogOpen(false);
      setSelectedCPEId(null);
      setSelectedCPEName('');
      loadCPEs();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to restore CPE');
    } finally {
      setRestoring(false);
    }
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return 'default';
    if (status.includes('Project')) return 'info';
    if (status.includes('Design') || status.includes('Ready')) return 'warning';
    if (status.includes('Execution') || status.includes('Testing')) return 'secondary';
    if (status.includes('Completed') || status.includes('Energized')) return 'success';
    return 'default';
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      setError('');

      const blob = await cpeService.generateReport(
        filters.updated_from || undefined,
        filters.updated_to || undefined,
        filters
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `CPE_Report_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  console.log('About to render, state:', { loading, error, cpesCount: cpes.length, total, page, rowsPerPage });

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            {t('cpe.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* View toggle */}
            <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Tooltip title={t('cpe.viewMode.list', 'List view')}>
                <IconButton
                  size="small"
                  onClick={() => handleViewModeChange('list')}
                  sx={{
                    borderRadius: 0,
                    bgcolor: viewMode === 'list' ? 'primary.main' : 'transparent',
                    color: viewMode === 'list' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': { bgcolor: viewMode === 'list' ? 'primary.dark' : 'action.hover' },
                    px: 1,
                  }}
                >
                  <ViewListIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('cpe.viewMode.board', 'Board view')}>
                <IconButton
                  size="small"
                  onClick={() => handleViewModeChange('board')}
                  sx={{
                    borderRadius: 0,
                    bgcolor: viewMode === 'board' ? 'primary.main' : 'transparent',
                    color: viewMode === 'board' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': { bgcolor: viewMode === 'board' ? 'primary.dark' : 'action.hover' },
                    px: 1,
                  }}
                >
                  <ViewKanbanIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {(user?.role === 'super_admin' || (user?.role === 'admin' && user?.can_download_reports)) && (
              <Button
                variant="outlined"
                startIcon={generatingReport ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                {generatingReport ? t('reports.generating') : t('reports.downloadReport')}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setFormDialogOpen(true)}
              >
                {t('cpe.addCpe')}
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('filters.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              label={t('filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 250 }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />

            {/* CPE Status */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('filters.cpeStatus')}</InputLabel>
              <Select
                value={filters.cpe_status || ''}
                onChange={(e) => handleFilterChange('cpe_status', e.target.value)}
                label={t('filters.cpeStatus')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {CPE_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Commercial Status */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('filters.commercialStatus')}</InputLabel>
              <Select
                value={filters.commercial_status || ''}
                onChange={(e) => handleFilterChange('commercial_status', e.target.value)}
                label={t('filters.commercialStatus')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {COMMERCIAL_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Plant Type */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('filters.plantType')}</InputLabel>
              <Select
                value={filters.plant_type?.[0] || ''}
                onChange={(e) => handleFilterChange('plant_type', e.target.value ? [e.target.value as PlantType] : undefined)}
                label={t('filters.plantType')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {PLANT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Internal Positioning */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('filters.internalPositioning')}</InputLabel>
              <Select
                value={filters.internal_positioning || ''}
                onChange={(e) => handleFilterChange('internal_positioning', e.target.value)}
                label={t('filters.internalPositioning')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="go">{t('cpe.positioning.go')}</MenuItem>
                <MenuItem value="no_go">{t('cpe.positioning.noGo')}</MenuItem>
              </Select>
            </FormControl>

            {/* Waldevar PP */}
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>{t('cpe.fields.isWaldevarPP', 'Waldevar PP')}</InputLabel>
              <Select
                value={filters.is_waldevar_pp !== undefined ? String(filters.is_waldevar_pp) : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  handleFilterChange('is_waldevar_pp', val === '' ? undefined : val === 'true');
                }}
                label={t('cpe.fields.isWaldevarPP', 'Waldevar PP')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="true">{t('common.yes')}</MenuItem>
                <MenuItem value="false">{t('common.no')}</MenuItem>
              </Select>
            </FormControl>

            {/* Power Range */}
            <TextField
              label={t('filters.minPower')}
              type="number"
              value={filters.min_power || ''}
              onChange={(e) => handleFilterChange('min_power', e.target.value ? Number(e.target.value) : undefined)}
              sx={{ minWidth: 150 }}
            />

            <TextField
              label={t('filters.maxPower')}
              type="number"
              value={filters.max_power || ''}
              onChange={(e) => handleFilterChange('max_power', e.target.value ? Number(e.target.value) : undefined)}
              sx={{ minWidth: 150 }}
            />

            {/* Account Manager */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel shrink>{t('filters.accountManager')}</InputLabel>
              <Select
                multiple
                value={filters.om_account_manager ? (Array.isArray(filters.om_account_manager) ? filters.om_account_manager : [filters.om_account_manager]) : []}
                onChange={(e) => {
                  const selected = e.target.value as string[];
                  handleFilterChange('om_account_manager', selected.length > 0 ? selected : undefined);
                }}
                input={<OutlinedInput label={t('filters.accountManager')} notched />}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || (selected as string[]).length === 0) {
                    return <em>{t('common.all')}</em>;
                  }
                  const selectedIds = selected as string[];
                  const selectedNames = selectedIds.map(id => users.find(u => u.id === id)?.name || users.find(u => u.id === id)?.email).filter(Boolean);
                  return selectedNames.join(', ');
                }}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Checkbox checked={(filters.om_account_manager as string[])?.includes(user.id) || false} />
                    <ListItemText primary={user.name || user.email} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Next Action Filter */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel shrink>{t('nextAction.label', 'Next Action')}</InputLabel>
              <Select
                value={filters.next_action_id || ''}
                onChange={(e) => {
                  const val = e.target.value || undefined;
                  // Update both keys atomically to avoid stale-closure overwrite
                  setFilters((prev) => ({ ...prev, next_action_id: val, overdue_next_action: undefined }));
                  setPage(0);
                }}
                displayEmpty
                input={<OutlinedInput label={t('nextAction.label', 'Next Action')} notched />}
              >
                <MenuItem value="">
                  <em>{t('nextAction.filter.all', 'All')}</em>
                </MenuItem>
                <MenuItem value="none">{t('nextAction.filter.none', 'No action set')}</MenuItem>
                {nextActionOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                    {!opt.is_active && (
                      <Chip label={t('settings.nextActionOptions.archived', 'Archived')} size="small" sx={{ ml: 1 }} />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Overdue Next Action toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label={t('nextAction.filter.overdue', 'Overdue Only')}
                color={filters.overdue_next_action ? 'error' : 'default'}
                variant={filters.overdue_next_action ? 'filled' : 'outlined'}
                onClick={() => {
                  setFilters((prev) => ({
                    ...prev,
                    overdue_next_action: prev.overdue_next_action ? undefined : true,
                  }));
                  setPage(0);
                }}
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            {/* Zone */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel shrink>{t('filters.zone')}</InputLabel>
              <Select
                multiple
                value={filters.zone_id ? (Array.isArray(filters.zone_id) ? filters.zone_id : [filters.zone_id]) : []}
                onChange={(e) => {
                  const selected = e.target.value as string[];
                  handleFilterChange('zone_id', selected.length > 0 ? selected : undefined);
                }}
                input={<OutlinedInput label={t('filters.zone')} notched />}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || (selected as string[]).length === 0) {
                    return <em>{t('common.all')}</em>;
                  }
                  const selectedIds = selected as string[];
                  const selectedNames = selectedIds.map(id => zones.find(z => z.id === id)?.name).filter(Boolean);
                  return selectedNames.join(', ');
                }}
              >
                {zones.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    <Checkbox checked={(filters.zone_id as string[])?.includes(zone.id) || false} />
                    <ListItemText primary={zone.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Updated From Date */}
            <TextField
              label={t('filters.updatedFrom')}
              type="date"
              value={filters.updated_from || ''}
              onChange={(e) => handleFilterChange('updated_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 200,
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                  cursor: 'pointer',
                }
              }}
            />

            {/* Updated To Date */}
            <TextField
              label={t('filters.updatedTo')}
              type="date"
              value={filters.updated_to || ''}
              onChange={(e) => handleFilterChange('updated_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 200,
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                  cursor: 'pointer',
                }
              }}
            />

            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setFilters({});
                setPage(0);
              }}
            >
              {t('filters.clearFilters')}
            </Button>
          </Box>
        </Paper>

        {/* Board View */}
        {viewMode === 'board' && (
          <CPEBoardView
            cpes={boardCPEs}
            zoneMap={zoneMap}
            loading={boardLoading}
            error={boardError}
            onCardClick={(cpe) => navigate(`/cpes/${cpe.id}`)}
            onStatusChange={handleBoardStatusChange}
          />
        )}

        {/* Table */}
        {viewMode === 'list' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cpe.image')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('cpe.fields.plantType')}</TableCell>
                <TableCell>{t('cpe.status')}</TableCell>
                <TableCell>{t('cpe.fields.commercialStatus')}</TableCell>
                <TableCell>{t('cpe.fields.power')}</TableCell>
                <TableCell>{t('cpe.fields.isWaldevarPP', 'Waldevar')}</TableCell>
                <TableCell>{t('nextAction.label', 'Next Action')}</TableCell>
                <TableCell>{t('cpe.fields.location')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : cpes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {t('cpe.noCpesFound')}
                  </TableCell>
                </TableRow>
              ) : (
                cpes.map((cpe) => (
                  <TableRow
                    key={cpe.id}
                    sx={{ opacity: cpe.deleted_at ? 0.5 : 1 }}
                  >
                    <TableCell>
                      {cpe.image_url ? (
                        <Avatar
                          src={cpeService.getImageUrl(cpe.image_url) || undefined}
                          variant="rounded"
                          sx={{ bgcolor: 'grey.300' }}
                        >
                          <ImageIcon />
                        </Avatar>
                      ) : (
                        <Avatar variant="rounded" sx={{ bgcolor: 'grey.300' }}>
                          <ImageIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {cpe.name}
                      </Typography>
                      {cpe.company_cui && (
                        <Typography variant="caption" color="text.secondary">
                          CUI: {cpe.company_cui}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {cpe.plant_type.map((type) => (
                        <Chip key={type} label={type} size="small" sx={{ mr: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell>
                      {cpe.cpe_status && (
                        <Chip
                          label={cpe.cpe_status}
                          color={getStatusColor(cpe.cpe_status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {cpe.commercial_status && (
                        <Chip label={cpe.commercial_status} size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{cpe.power?.toLocaleString() || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={cpe.is_waldevar_pp ? t('common.yes') : t('common.no')}
                        color={cpe.is_waldevar_pp ? 'primary' : 'default'}
                        size="small"
                        sx={cpe.is_waldevar_pp ? { bgcolor: '#1d388d', color: '#fff' } : {}}
                      />
                    </TableCell>
                    {/* Next Action */}
                    <TableCell>
                      {cpe.next_action_id ? (() => {
                        const today = new Date().toISOString().split('T')[0];
                        const isOverdue = cpe.next_action_due_date != null && cpe.next_action_due_date < today;
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Chip
                              label={cpe.nextAction?.name || cpe.next_action_id}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                            {cpe.next_action_due_date && (
                              <Typography
                                variant="caption"
                                sx={{ color: isOverdue ? 'error.main' : 'text.secondary', fontWeight: isOverdue ? 'bold' : 'normal' }}
                              >
                                {isOverdue ? `${t('nextAction.overdue', 'Overdue')} — ${cpe.next_action_due_date}` : cpe.next_action_due_date}
                              </Typography>
                            )}
                          </Box>
                        );
                      })() : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={() => navigate('/', {
                          state: {
                            centerLat: Number(cpe.latitude),
                            centerLng: Number(cpe.longitude),
                            zoom: 10,
                            cpeId: cpe.id
                          }
                        })}
                        title={`${Number(cpe.latitude).toFixed(6)}, ${Number(cpe.longitude).toFixed(6)} — View on map`}
                      >
                        {/* Coordinates until the location has been reverse-geocoded */}
                        {[cpe.locality, cpe.county].filter(Boolean).join(', ')
                          || `${Number(cpe.latitude).toFixed(6)}, ${Number(cpe.longitude).toFixed(6)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/cpes/${cpe.id}`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isAdmin && !cpe.deleted_at && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/cpes/${cpe.id}/edit`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(isSuperAdmin || user?.can_delete_cpe) && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(cpe.id, cpe.name)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        {isSuperAdmin && cpe.deleted_at && (
                          <Tooltip title="Restore">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleRestore(cpe.id, cpe.name)}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
        )}
      </Box>

      {/* CPE Form Dialog */}
      <CPEFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          loadCPEs(); // Reload list after closing
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('common.deleteConfirmMessage')} <strong>{selectedCPEName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('common.loading') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => !restoring && setRestoreDialogOpen(false)}
      >
        <DialogTitle>{t('cpe.restoreCpe')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cpe.confirmRestore')} <strong>{selectedCPEName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleRestoreConfirm}
            color="success"
            variant="contained"
            disabled={restoring}
          >
            {restoring ? t('common.loading') : t('cpe.restoreCpe')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

